import { Static, Type } from '@sinclair/typebox';
import {
  ImportDuplicateStrategy,
  ImportMatchKeys,
  ImportRowAction,
  ImportRowErrorKey,
  ImportTargetField,
  ProCustomerImportStatus,
} from './translation_namespace/pro_customer_import_status_namespace';
import { parsePrefixedPositiveId } from './utils';

/**
 * Import de base clients CSV / XLSX — contrat partagé backend ↔ frontend.
 *
 * Une ligne importée est valide si elle a **email OU téléphone OU (nom + prénom)** — même règle
 * que la création manuelle (`hasProCustomerIdentifier`, cf. plan
 * docs/to_implement/customer-import-csv-xlsx-plan.md §2).
 */

// ── Bornes (source de vérité unique, appliquées backend + affichées front) ──
export const CUSTOMER_IMPORT_MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 Mo
export const CUSTOMER_IMPORT_MAX_ROWS = 20_000;
export const CUSTOMER_IMPORT_MAX_NEW_TAGS = 50;
/** Nombre de lignes d'exemple retournées à l'upload et en préviz dry-run. */
export const CUSTOMER_IMPORT_SAMPLE_ROWS = 5;
export const CUSTOMER_IMPORT_PREVIEW_ROWS = 50;

/**
 * Sous-ensemble CSV/XLSX de `SUPPORT_ATTACHMENT_ALLOWED_MIME_TYPES`.
 * NB : le Content-Type multer est fourni par le client (spoofable) — filtre de
 * confort seulement, la vraie défense est le parsing serveur borné.
 */
export const CUSTOMER_IMPORT_ALLOWED_MIME_TYPES = [
  'text/csv',
  'application/csv',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

// ── Schémas ──────────────────────────────────────────────────────────────

/**
 * Cible de mapping d'une colonne : soit un champ standard de la fiche
 * (`ImportTargetField`), soit un champ personnalisé du pro, désigné
 * `custom:<pro_customer_custom_field_id>`.
 *
 * Le schéma TypeBox porte donc `Type.String()` et non l'enum : une cible
 * `custom:` n'est valide QUE pour le pro propriétaire du champ, ce qu'un enum
 * statique ne peut pas exprimer. La validation d'appartenance est faite côté
 * service, qui écarte les cibles inconnues.
 */
export const IMPORT_CUSTOM_FIELD_TARGET_PREFIX = 'custom:';

export type ImportTargetFieldValue = ImportTargetField | `${typeof IMPORT_CUSTOM_FIELD_TARGET_PREFIX}${number}`;

export const buildImportCustomFieldTarget = (proCustomerCustomFieldId: number): ImportTargetFieldValue =>
  `${IMPORT_CUSTOM_FIELD_TARGET_PREFIX}${proCustomerCustomFieldId}`;

/** Id du champ personnalisé visé, ou `null` si la cible n'en est pas une. */
export const parseImportCustomFieldTarget = (target: string): number | null =>
  parsePrefixedPositiveId(target, IMPORT_CUSTOM_FIELD_TARGET_PREFIX);

export const isStandardImportTarget = (target: string): target is ImportTargetField =>
  (Object.values(ImportTargetField) as string[]).includes(target);

export const DetectedColumnSchema = Type.Object({
  source_header: Type.String(),
  normalized_header: Type.String(),
  /** `ImportTargetField` ou `custom:<id>` — cf. `ImportTargetFieldValue`. */
  suggested_field: Type.Optional(Type.String()),
  confidence: Type.Number({ minimum: 0, maximum: 1 }),
  sample_values: Type.Array(Type.String()),
});
export type DetectedColumnObject = Static<typeof DetectedColumnSchema>;

export const ProCustomerImportOptionsSchema = Type.Object({
  duplicate_strategy: Type.Enum(ImportDuplicateStrategy),
  match_keys: Type.Enum(ImportMatchKeys),
  create_missing_tags: Type.Boolean(),
});
export type ProCustomerImportOptionsObject = Static<typeof ProCustomerImportOptionsSchema>;

export const ProCustomerImportMappingRequestSchema = Type.Object({
  /**
   * Clé = en-tête source de la colonne, valeur = champ cible (`tag` / `ignore`
   * inclus), ou `custom:<pro_customer_custom_field_id>` pour un champ
   * personnalisé — cf. `ImportTargetFieldValue`.
   */
  mapping: Type.Record(Type.String(), Type.String()),
  options: ProCustomerImportOptionsSchema,
});
export type ProCustomerImportMappingRequestObject = Static<typeof ProCustomerImportMappingRequestSchema>;

export const ImportRowErrorSchema = Type.Object({
  row_number: Type.Number(),
  field: Type.Optional(Type.String()),
  error_key: Type.Enum(ImportRowErrorKey),
  source_value: Type.Optional(Type.String()),
});
export type ImportRowErrorObject = Static<typeof ImportRowErrorSchema>;

export const ImportPreviewRowSchema = Type.Object({
  row_number: Type.Number(),
  action: Type.Enum(ImportRowAction),
  /** Ligne APRÈS formattage magique : clé = champ cible, valeur = valeur affichable. */
  values: Type.Record(Type.String(), Type.String()),
  tags: Type.Array(Type.String()),
});
export type ImportPreviewRowObject = Static<typeof ImportPreviewRowSchema>;

export const ProCustomerImportResponseSchema = Type.Object({
  pro_customer_import_id: Type.Number(),
  status: Type.Enum(ProCustomerImportStatus),
  original_filename: Type.String(),
  total_rows: Type.Number(),
  processed_rows: Type.Number(),
  created_count: Type.Number(),
  updated_count: Type.Number(),
  skipped_count: Type.Number(),
  error_count: Type.Number(),
  has_report: Type.Boolean(),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
  completed_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'completed_at', with_time: true } }),
  ),
});
export type ProCustomerImportResponseObject = Static<typeof ProCustomerImportResponseSchema>;

export const ListProCustomerImportResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(ProCustomerImportResponseSchema),
});
export type ListProCustomerImportResponseObject = Static<typeof ListProCustomerImportResponseSchema>;

export const ProCustomerImportUploadResponseSchema = Type.Object({
  import: ProCustomerImportResponseSchema,
  columns: Type.Array(DetectedColumnSchema),
  sample_rows: Type.Array(Type.Record(Type.String(), Type.String())),
  /** Valeurs : `ImportTargetField` ou `custom:<id>` — cf. `ImportTargetFieldValue`. */
  suggested_mapping: Type.Record(Type.String(), Type.String()),
  suggested_options: ProCustomerImportOptionsSchema,
});
export type ProCustomerImportUploadResponseObject = Static<typeof ProCustomerImportUploadResponseSchema>;

export const ProCustomerImportDryRunResponseSchema = Type.Object({
  total: Type.Number(),
  new_count: Type.Number(),
  update_count: Type.Number(),
  duplicate_count: Type.Number(),
  error_count: Type.Number(),
  /**
   * Lignes valides mais sans email ni téléphone : jamais rapprochables de la
   * base via `match_keys` → doublons garantis à chaque ré-import du même
   * fichier. Affiché en bandeau d'avertissement côté front.
   */
  no_match_key_count: Type.Number(),
  new_tag_count: Type.Number(),
  preview_rows: Type.Array(ImportPreviewRowSchema),
  row_errors: Type.Array(ImportRowErrorSchema),
});
export type ProCustomerImportDryRunResponseObject = Static<typeof ProCustomerImportDryRunResponseSchema>;

export const ProCustomerImportReportResponseSchema = Type.Object({
  report_download_url: Type.String(),
});
export type ProCustomerImportReportResponseObject = Static<typeof ProCustomerImportReportResponseSchema>;
